import React, { useCallback, useRef, useState } from 'react';
import { Button, Container } from 'reactstrap';
import { connect } from 'react-redux';
import {
  AsyncTableInfinite,
  ConfirmationModal,
  Intl,
  ContactModal,
  AudioModal,
  FavouriteUploadAudioModal,
  FavouriteUploadExcelContactModal,
} from 'components';
import { handlePromise } from 'utils';
import ListPhoneNumbersService from 'api/ListPhoneNumbers';
import SettingsActions from 'store/reducers/Settings';

const Favourite = ( {
  phones,
  audios,
  phonesLoading,
  audiosLoading,
  onFetchPhones,
  onFetchAudios,
  onReloadAudios,
  onReloadPhones,
  onDeletePhone,
  onDeleteAudio,
  toggleLoading,
  toggleErrorAlert,
} ) => {
  const [deleteModal, setDeleteModal] = useState( false );
  const [contactModal, setContactModal] = useState( false );
  const [audioModal, setAudioModal] = useState( false );
  const [uploadAudioModal, setUploadAudioModal] = useState( false );
  const [uploadContactModal, setUploadContactModal] = useState( false );
  const [invalidContactsModalOpened, setInvalidContactsModalOpened] = useState(
    false,
  );
  const [
    duplicatedContactsModalOpened,
    setDuplicatedContactsModalOpened,
  ] = useState( false );
  const deleteCb = useRef( null );
  const contactsRef = useRef( null );
  const duplicatedRef = useRef( null );
  const audioRef = useRef( null );

  const exportPhones = useCallback(
    async ( id ) => {
      toggleLoading( true );
      const [errors, response, responseData] = await handlePromise(
        ListPhoneNumbersService.getListPhoneNumber( id, { download: true } ),
      );
      toggleLoading( false );
      if ( !response.ok ) return toggleErrorAlert( errors );
      window.open( responseData.uri );
    },
    [toggleLoading, toggleErrorAlert],
  );

  const downloadFavorite = useCallback(
    async ( id ) => {
      toggleLoading( true );
      const [errors, response, responseData] = await handlePromise(
        ListPhoneNumbersService.exportFavorite( id ),
      );
      toggleLoading( false );
      if ( !response.ok ) return toggleErrorAlert( errors );
      window.open( responseData.url );
    },
    [toggleLoading, toggleErrorAlert],
  );

  const toggleDeleteModal = useCallback( ( cb ) => {
    deleteCb.current = cb;
    setDeleteModal( ( state ) => !state );
  }, [] );

  const toggleContactModal = useCallback( ( contacts ) => {
    contactsRef.current = contacts;
    setContactModal( ( state ) => !state );
  }, [] );

  const toggleAudioModal = useCallback( ( audio ) => {
    audioRef.current = audio;
    setAudioModal( ( state ) => !state );
  }, [] );

  const toggleUploadAudioModal = useCallback( () => {
    setUploadAudioModal( ( state ) => !state );
  }, [] );

  const toggleUploadContactModal = useCallback( () => {
    setUploadContactModal( ( state ) => !state );
  }, [] );

  const toggleInvalidContactsModal = useCallback( ( invalidContacts = [] ) => {
    contactsRef.current = invalidContacts;
    setInvalidContactsModalOpened( ( state ) => !state );
  }, [] );

  const toggleDuplicatedContactsModal = useCallback(
    ( duplicatedContacts = [] ) => {
      duplicatedRef.current = duplicatedContacts;
      setDuplicatedContactsModalOpened( ( state ) => !state );
    },
    [],
  );

  return (
    <>
      <Container fluid className="mt-3">
        <AsyncTableInfinite
          data={phones.data}
          total={phones.total}
          isLoading={phonesLoading}
          onFetchData={onFetchPhones}
          defaultSortBy={[{ id: 'createdAt', desc: true }]}
          titleContent={<Intl id="phonesList" />}
          tableName="phonesList"
          rightContent={(
            <Button
              color="app-default"
              size="app-sm"
              onClick={toggleUploadContactModal}
            >
              <Intl id="uploadFile" />
            </Button>
          )}
          columns={[
            {
              Header: 'phonesListName',
              accessor: 'name',
              style: { textAlign: 'left', paddingLeft: 20 },
              width: 80,
              Cell: ( { cell } ) => ( cell.value ? (
                <span
                  className="text-mp-blue-1"
                  style={{ cursor: 'pointer' }}
                  onClick={() => toggleContactModal( cell.row.original )}
                >
                  {cell.value}
                </span>
              ) : (
                '-'
              ) ),
            },
            {
              accessor: 'id',
              disableSorting: true,
              Header: '',
              width: 16,
              Cell: ( { cell } ) => (
                <>
                  <Button
                    color="outline-link"
                    className="p-0 lh-110"
                    onClick={() => exportPhones( cell.value )}
                  >
                    <Intl id="download" />
                  </Button>
                  <Button
                    color="outline-link"
                    className="p-0 lh-110"
                    onClick={() => {
                      toggleDeleteModal( () => onDeletePhone( cell.value, toggleDeleteModal ) );
                    }}
                  >
                    <Intl id="delete" />
                  </Button>
                </>
              ),
            },
          ]}
          headerWidth={['col-9', 'col-3']}
        />
      </Container>

      <Container fluid className="mt-5">
        <AsyncTableInfinite
          data={audios.data}
          total={audios.total}
          isLoading={audiosLoading}
          onFetchData={onFetchAudios}
          defaultSortBy={[{ id: 'createdAt', desc: true }]}
          titleContent={<Intl id="audioFilesList" />}
          tableName="audios"
          rightContent={(
            <Button
              color="app-default"
              size="app-sm"
              onClick={toggleUploadAudioModal}
            >
              <Intl id="uploadFile" />
            </Button>
          )}
          columns={[
            {
              Header: 'audioFileName',
              accessor: 'userDefineName',
              style: { textAlign: 'left', paddingLeft: 20 },
              Cell: ( { cell } ) => ( cell.value ? (
                <span
                  className="text-mp-blue-1"
                  style={{ cursor: 'pointer' }}
                  onClick={() => toggleAudioModal( cell.row.original )}
                >
                  {cell.value}
                </span>
              ) : (
                '-'
              ) ),
            },
            {
              accessor: 'id',
              disableSorting: true,
              Header: '',
              width: 16,
              Cell: ( { cell } ) => (
                <>
                  <Button
                    color="outline-link"
                    className="p-0 lh-110"
                    onClick={() => {
                      downloadFavorite( cell.value );
                    }}
                  >
                    <Intl id="download" />
                  </Button>
                  <Button
                    color="outline-link"
                    className="p-0 lh-110"
                    onClick={() => {
                      toggleDeleteModal( () => onDeleteAudio( cell.value, toggleDeleteModal ) );
                    }}
                  >
                    <Intl id="delete" />
                  </Button>
                </>
              ),
            },
          ]}
          headerWidth={['col-9', 'col-3']}
        />
      </Container>

      {deleteModal && (
        <ConfirmationModal
          open={deleteModal}
          title="deleteModalFile.confirmation"
          onConfirm={deleteCb.current}
          onClose={toggleDeleteModal}
        />
      )}

      {contactModal && (
        <ContactModal
          data={contactsRef.current.phoneNumbers}
          download={false}
          open={contactModal}
          translateTitle={false}
          modalTitle={contactsRef.current.name}
          onClose={toggleContactModal}
        />
      )}

      {audioModal && (
        <AudioModal
          uri={audioRef.current ? audioRef.current.uri : null}
          modalTitle={audioRef.current.originalName}
          open={audioModal}
          onClose={toggleAudioModal}
        />
      )}

      {uploadAudioModal && (
        <FavouriteUploadAudioModal
          open={uploadAudioModal}
          onClose={toggleUploadAudioModal}
          onReload={() => {
            toggleUploadAudioModal();
            onReloadAudios();
          }}
        />
      )}

      {uploadContactModal && (
        <FavouriteUploadExcelContactModal
          open={uploadContactModal}
          onClose={toggleUploadContactModal}
          onReload={( duplicateNumbers, invalidNumbers ) => {
            if ( invalidNumbers ) toggleInvalidContactsModal( invalidNumbers );
            if ( duplicateNumbers ) toggleDuplicatedContactsModal( duplicateNumbers );
            toggleUploadContactModal();
            onReloadPhones();
          }}
        />
      )}

      {duplicatedContactsModalOpened && (
        <ContactModal
          data={duplicatedRef.current}
          modalTitle="duplicatedPhones"
          open={duplicatedContactsModalOpened}
          onClose={toggleDuplicatedContactsModal}
        />
      )}

      {invalidContactsModalOpened && (
        <ContactModal
          data={contactsRef.current}
          modalTitle="wrongPhones"
          open={invalidContactsModalOpened}
          onClose={toggleInvalidContactsModal}
          type="error"
          zIndex={5100}
        />
      )}
    </>
  );
};

const mapDispatchToProps = {
  toggleLoading: SettingsActions.toggleLoading,
  toggleErrorAlert: SettingsActions.toggleErrorAlert,
};

export default connect( null, mapDispatchToProps )( Favourite );
